import {parseAxiosPromise} from '@/axiosInstance'
import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'
import {useInputSearch} from '@/hooks/useInputSearch'
import {httpGetWorkAreas, HttpGetWorkAreasOptions} from '@/features/users/http/users.http'
import {z} from 'zod'
import {PaginationResponse} from '@/types'
import {UserWorkArea} from '@/features/users/types'

export const HttpGetWorkAreasResponse = z.object({
    data: z.array(UserWorkArea),
    meta: PaginationResponse
})
export type HttpGetWorkAreasResponse = z.infer<typeof HttpGetWorkAreasResponse>

export const useGetWorkAreas = (
    promiseArgs: Omit<HttpGetWorkAreasOptions, 'page' | 'search'>,
    options?: Pick<UseInfiniteQueryOptions<HttpGetWorkAreasResponse>, 'enabled'>
) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.USER_WORK_AREAS, searchValue],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetWorkAreas({
                    page: pageParam,
                    search: searchValue,
                    limit: promiseArgs.limit,
                    responseType: promiseArgs.responseType
                }),
                responseShape: HttpGetWorkAreasResponse,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
